<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

const props = defineProps({
  tramite: { type: Object, required: true },
  isAdvancing: { type: Boolean, default: false },
});

const emit = defineEmits(['advance']);

const { t } = useI18n();

const comment = ref('');

const stages = computed(() =>
  [...(props.tramite.stages || [])].sort(
    (a, b) => (a.position || 0) - (b.position || 0)
  )
);

const currentStage = computed(
  () =>
    props.tramite.current_stage ||
    stages.value.find(s => s.is_current) ||
    null
);

const currentActions = computed(() => currentStage.value?.actions || []);

// Humanize an action name like "approve_request" → "Approve request".
const humanize = name =>
  (name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());

const stageDotClass = stage => {
  if (stage.is_current) return 'bg-n-blue-9 ring-4 ring-n-blue-9/20';
  if (stage.state === 'completed') return 'bg-n-teal-9';
  if (stage.overdue) return 'bg-n-ruby-9';
  return 'bg-n-slate-7';
};

const stageStateLabel = stage => {
  if (stage.is_current) return t('TRAMITES.STAGES.ACTIVE');
  if (stage.state === 'completed') return t('TRAMITES.STAGES.COMPLETED');
  return t('TRAMITES.STAGES.PENDING');
};

const formatDate = date =>
  date
    ? new Date(date).toLocaleDateString(undefined, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : null;

const onAdvance = action => {
  emit('advance', { actionName: action.action, comment: comment.value.trim() });
  comment.value = '';
};
</script>

<template>
  <section class="flex flex-col gap-4">
    <h3 class="text-sm font-semibold text-n-slate-12">
      {{ t('TRAMITES.DETAIL.SECTIONS.STAGES') }}
    </h3>

    <ol class="flex flex-col gap-0">
      <li
        v-for="(stage, idx) in stages"
        :key="stage.id"
        class="relative flex gap-3 pb-4"
      >
        <!-- connector line -->
        <span
          v-if="idx < stages.length - 1"
          class="absolute left-[5px] top-4 bottom-0 w-px bg-n-weak"
        />
        <!-- dot -->
        <span
          class="relative z-10 w-2.5 h-2.5 mt-1 rounded-full shrink-0"
          :class="stageDotClass(stage)"
        />
        <div class="flex flex-col flex-1 min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <span
              class="text-sm"
              :class="
                stage.is_current
                  ? 'font-semibold text-n-slate-12'
                  : 'text-n-slate-11'
              "
            >
              {{ stage.name }}
            </span>
            <span class="text-xs text-n-slate-10">
              {{ stageStateLabel(stage) }}
            </span>
            <Badge v-if="stage.overdue" color="red" size="small">
              {{ t('TRAMITES.STAGES.OVERDUE') }}
            </Badge>
            <Badge
              v-if="stage.doc_upload_required"
              color="yellow"
              size="small"
            >
              {{ t('TRAMITES.STAGES.DOC_REQUIRED') }}
            </Badge>
          </div>
          <span
            v-if="stage.sla_deadline"
            class="text-xs"
            :class="stage.overdue ? 'text-n-ruby-9' : 'text-n-slate-10'"
          >
            {{ t('TRAMITES.FIELDS.SLA_DEADLINE') }}:
            {{ formatDate(stage.sla_deadline) }}
          </span>
        </div>
      </li>
    </ol>

    <!-- Current stage actions -->
    <div
      v-if="currentStage"
      class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-alpha-1"
    >
      <span class="text-xs font-medium uppercase text-n-slate-10">
        {{ t('TRAMITES.STAGES.CURRENT') }}: {{ currentStage.name }}
      </span>

      <textarea
        v-model="comment"
        rows="2"
        :placeholder="t('TRAMITES.STAGES.COMMENT_PLACEHOLDER')"
        class="w-full px-3 py-2 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
      />

      <div v-if="currentActions.length" class="flex flex-wrap gap-2">
        <Button
          v-for="action in currentActions"
          :key="action.action"
          :label="humanize(action.action)"
          size="sm"
          color="blue"
          :is-loading="isAdvancing"
          :disabled="isAdvancing"
          @click="onAdvance(action)"
        />
      </div>
      <p v-else class="text-xs italic text-n-slate-10">
        {{ t('TRAMITES.STAGES.NO_ACTIONS') }}
      </p>
    </div>
  </section>
</template>
